// A self-contained dark basemap for previews, so the sample-agency maps render
// with no Mapbox token and no network (Studio screenshots are generated
// headless, anywhere). Real boards keep Mapbox's own dark style.
//
// Geometry is synthesized around the demo station: a river, a lake, highways,
// a street grid in town, the built-up area, and county lines, styled to read
// like Mapbox dark-v11. Town names are HTML markers (DEMO_PLACES), since an
// offline style has no glyphs for text layers.

import { DEMO_STATION, offset } from "./demoAgency";

const at = (bearing, dist) => offset(DEMO_STATION.lat, DEMO_STATION.lng, bearing, dist);
const ll = (p) => [p.lng, p.lat];

// Polyline through points given as [bearing, distMi] from the station.
const line = (pts) => pts.map(([b, d]) => ll(at(b, d)));
const feature = (geometry, properties = {}) => ({ type: "Feature", geometry, properties });

function streetGrid(radiusMi, stepMi) {
  const out = [];
  for (let k = -radiusMi; k <= radiusMi + 1e-9; k += stepMi) {
    const n = offset(DEMO_STATION.lat, DEMO_STATION.lng, 0, k);
    const e = offset(DEMO_STATION.lat, DEMO_STATION.lng, 90, k);
    // East-west street at k miles north, north-south street at k miles east.
    out.push(feature({ type: "LineString", coordinates: [ll(offset(n.lat, n.lng, 270, radiusMi)), ll(offset(n.lat, n.lng, 90, radiusMi))] }));
    out.push(feature({ type: "LineString", coordinates: [ll(offset(e.lat, e.lng, 180, radiusMi)), ll(offset(e.lat, e.lng, 0, radiusMi))] }));
  }
  return out;
}

function blob(radiusMi, wobble, steps = 48) {
  const ring = [];
  for (let i = 0; i <= steps; i += 1) {
    const b = (360 * i) / steps;
    const r = radiusMi * (1 + wobble * Math.sin((i * 5 * Math.PI) / steps) + wobble * 0.6 * Math.cos((i * 3 * Math.PI) / steps));
    ring.push(ll(at(b, r)));
  }
  return { type: "Polygon", coordinates: [ring] };
}

function lakeAt(bearing, dist, radiusMi) {
  const c = at(bearing, dist);
  const ring = [];
  for (let i = 0; i <= 36; i += 1) {
    const r = radiusMi * (1 + 0.25 * Math.sin(i * 0.9) + 0.15 * Math.cos(i * 1.7));
    ring.push(ll(offset(c.lat, c.lng, i * 10, r)));
  }
  return { type: "Polygon", coordinates: [ring] };
}

function countyLines() {
  const out = [];
  for (let k = -8; k <= 8; k += 1) {
    const n = offset(DEMO_STATION.lat, DEMO_STATION.lng, 0, k * 30 + 11);
    const e = offset(DEMO_STATION.lat, DEMO_STATION.lng, 90, k * 30 - 7);
    out.push(feature({ type: "LineString", coordinates: [ll(offset(n.lat, n.lng, 270, 260)), ll(offset(n.lat, n.lng, 90, 260))] }));
    out.push(feature({ type: "LineString", coordinates: [ll(offset(e.lat, e.lng, 180, 260)), ll(offset(e.lat, e.lng, 0, 260))] }));
  }
  return out;
}

let cached = null;

export function demoMapStyle() {
  if (cached) return cached;
  const fc = (features) => ({ type: "geojson", data: { type: "FeatureCollection", features } });
  cached = {
    version: 8,
    name: "Ladder demo dark",
    sources: {
      urban: fc([feature(blob(7.5, 0.12)), feature(blob(2.2, 0.2)), ...[[20, 27], [240, 14], [270, 30], [185, 12], [305, 44], [232, 27]].map(([b, d]) => {
        const c = at(b, d);
        const ring = [];
        for (let i = 0; i <= 24; i += 1) {
          const p = offset(c.lat, c.lng, (360 * i) / 24, 1.4 + 0.3 * Math.sin(i));
          ring.push(ll(p));
        }
        return feature({ type: "Polygon", coordinates: [ring] });
      })]),
      water: fc([
        feature({ type: "LineString", coordinates: line([[285, 240], [262, 130], [250, 60], [245, 40], [235, 25], [215, 12], [190, 6], [160, 5], [130, 9], [115, 22], [110, 45], [100, 70], [105, 150], [112, 240]]) }, { kind: "river" }),
        feature({ type: "LineString", coordinates: line([[315, 70], [320, 40], [330, 18], [345, 6], [20, 3]]) }, { kind: "creek" }),
        feature(lakeAt(135, 6.5, 1.3), { kind: "lake" }),
        feature(lakeAt(330, 38, 2.4), { kind: "lake" }),
      ]),
      roads: fc([
        // Interstate SW-NE, US highway E-W, state highway N-S, a loop.
        feature({ type: "LineString", coordinates: line([[225, 260], [225, 30], [220, 8], [200, 1.5], [60, 2], [45, 30], [45, 260]]) }, { cls: "motorway" }),
        feature({ type: "LineString", coordinates: line([[270, 260], [270, 20], [268, 3], [90, 3], [90, 260]]) }, { cls: "trunk" }),
        feature({ type: "LineString", coordinates: line([[0, 260], [2, 20], [5, 2], [178, 4], [180, 260]]) }, { cls: "trunk" }),
        feature({ type: "LineString", coordinates: Array.from({ length: 41 }, (_, i) => ll(at(i * 9, 9 + 0.8 * Math.sin(i)))) }, { cls: "primary" }),
        feature({ type: "LineString", coordinates: line([[300, 260], [300, 50], [295, 30], [290, 18]]) }, { cls: "primary" }),
        feature({ type: "LineString", coordinates: line([[140, 240], [150, 40], [160, 18], [170, 9]]) }, { cls: "primary" }),
        ...streetGrid(6, 0.5).map((f) => ({ ...f, properties: { cls: "street" } })),
        ...streetGrid(2, 0.125).map((f) => ({ ...f, properties: { cls: "local" } })),
      ]),
      counties: fc(countyLines()),
    },
    layers: [
      { id: "bg", type: "background", paint: { "background-color": "#12151b" } },
      { id: "urban", type: "fill", source: "urban", paint: { "fill-color": "#171b22" } },
      { id: "counties", type: "line", source: "counties", paint: { "line-color": "#3a3f4b", "line-width": 1, "line-dasharray": [3, 2] } },
      { id: "lake", type: "fill", source: "water", filter: ["==", ["get", "kind"], "lake"], paint: { "fill-color": "#0c1c2b" } },
      {
        id: "river", type: "line", source: "water", filter: ["!=", ["get", "kind"], "lake"],
        paint: { "line-color": "#0f2436", "line-width": ["interpolate", ["linear"], ["zoom"], 6, 1.5, 12, ["case", ["==", ["get", "kind"], "river"], 9, 4]] },
      },
      {
        id: "local", type: "line", source: "roads", filter: ["==", ["get", "cls"], "local"], minzoom: 12.5,
        paint: { "line-color": "#23272f", "line-width": ["interpolate", ["linear"], ["zoom"], 12.5, 0.6, 16, 5] },
      },
      {
        id: "streets", type: "line", source: "roads", filter: ["==", ["get", "cls"], "street"], minzoom: 10,
        paint: { "line-color": "#2a2f38", "line-width": ["interpolate", ["linear"], ["zoom"], 10, 0.6, 15, 6] },
      },
      {
        id: "primary", type: "line", source: "roads", filter: ["==", ["get", "cls"], "primary"],
        paint: { "line-color": "#2b3039", "line-width": ["interpolate", ["linear"], ["zoom"], 6, 0.6, 14, 6] },
      },
      {
        id: "trunk", type: "line", source: "roads", filter: ["==", ["get", "cls"], "trunk"],
        paint: { "line-color": "#353b46", "line-width": ["interpolate", ["linear"], ["zoom"], 6, 1, 14, 8] },
      },
      {
        id: "motorway", type: "line", source: "roads", filter: ["==", ["get", "cls"], "motorway"],
        paint: { "line-color": "#434a57", "line-width": ["interpolate", ["linear"], ["zoom"], 6, 1.4, 14, 10] },
      },
    ],
  };
  return cached;
}

// Town labels (these match the sample storm reports' cities).
export const DEMO_PLACES = [
  { name: "Riverside", ...at(0, 0.01), major: true },
  { name: "Clearwater", ...at(240, 14) },
  { name: "Viola", ...at(232, 27) },
  { name: "Cheney", ...at(270, 30) },
  { name: "Haysville", ...at(185, 12) },
  { name: "Mount Hope", ...at(305, 44) },
  { name: "Andover", ...at(20, 27) },
  { name: "Hutchinson", ...at(325, 48), major: true },
  { name: "Newton", ...at(0, 26) },
  { name: "El Dorado", ...at(80, 30) },
  { name: "Wellington", ...at(185, 32) },
  { name: "Salina", ...at(345, 95), major: true },
  { name: "Emporia", ...at(55, 95), major: true },
  { name: "Pratt", ...at(270, 80) },
  { name: "Great Bend", ...at(310, 115), major: true },
  { name: "Ponca City", ...at(170, 75) },
  { name: "Enid", ...at(205, 110), major: true },
];

// Any token string works: the demo style loads nothing from Mapbox.
export const DEMO_MAP_TOKEN = "pk.demo";
